// Serviço para gerenciar notas fiscais no Supabase
import { supabase } from '../lib/supabase';
import type { NotaFiscal, ItemNota } from '../types';

// Interface para nota fiscal no banco de dados
interface NotaFiscalDB {
  id: string;
  tipo: string;
  numero: string;
  serie: string;
  chave_acesso?: string;
  emitente_id: string;
  destinatario_id: string;
  data_emissao: string;
  data_vencimento?: string;
  subtotal: number;
  desconto: number;
  total: number;
  status: string;
  forma_pagamento?: string;
  created_at: string;
  updated_at: string;
}

// Interface para item de nota fiscal no banco de dados
interface ItemNotaFiscalDB {
  id: string;
  nota_fiscal_id: string;
  tipo: 'produto' | 'servico';
  produto_id?: string;
  servico_id?: string;
  descricao: string;
  quantidade: number;
  unidade_medida: string;
  valor_unitario: number;
  valor_total: number;
  ncm?: string;
  cfop?: string;
  created_at: string;
}

// Buscar todas as notas fiscais da empresa atual
export async function getNotasFiscais(empresaId: string): Promise<NotaFiscal[]> {
  try {
    // Buscar notas sem joins complexos para evitar recursão RLS
    const { data: notas, error } = await supabase
      .from('notas_fiscais')
      .select('id, tipo, numero, serie, chave_acesso, empresa_id, cliente_id, data_emissao, data_vencimento, subtotal, desconto, total, status, forma_pagamento, created_at, updated_at')
      .eq('empresa_id', empresaId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[notasFiscaisService] Erro ao buscar notas fiscais:', error);
      // Retornar array vazio em vez de quebrar
      return [];
    }

    if (!notas || notas.length === 0) {
      return [];
    }

    // Buscar dados relacionados separadamente para evitar recursão
    const notasCompletas = await Promise.all(
      notas.map(async (nota: any) => {
        try {
          // Buscar empresa
          let empresaData: any = null;
          if (nota.empresa_id) {
            try {
              const { data: empresa, error: empresaError } = await supabase
                .from('empresas')
                .select('id, razao_social, nome_fantasia, cnpj, inscricao_estadual, email, telefone, endereco_id')
                .eq('id', nota.empresa_id)
                .single();

              if (!empresaError && empresa) {
                empresaData = empresa;
                // Buscar endereço da empresa separadamente
                if (empresa.endereco_id) {
                  const { data: endereco, error: enderecoError } = await supabase
                    .from('enderecos')
                    .select('cep, logradouro, numero, complemento, bairro, cidade, estado')
                    .eq('id', empresa.endereco_id)
                    .single();

                  if (!enderecoError && endereco) {
                    empresaData.enderecos = endereco;
                  }
                }
              }
            } catch (err) {
              console.warn('[notasFiscaisService] Erro ao buscar empresa:', err);
            }
          }

          // Buscar cliente
          let clienteData: any = null;
          if (nota.cliente_id) {
            try {
              const { data: cliente, error: clienteError } = await supabase
                .from('clientes')
                .select('id, nome, tipo, cpf, cnpj, email, telefone, endereco_id')
                .eq('id', nota.cliente_id)
                .single();

              if (!clienteError && cliente) {
                clienteData = cliente;
                // Buscar endereço do cliente separadamente
                if (cliente.endereco_id) {
                  const { data: endereco, error: enderecoError } = await supabase
                    .from('enderecos')
                    .select('cep, logradouro, numero, complemento, bairro, cidade, estado')
                    .eq('id', cliente.endereco_id)
                    .single();

                  if (!enderecoError && endereco) {
                    clienteData.enderecos = endereco;
                  }
                }
              }
            } catch (err) {
              console.warn('[notasFiscaisService] Erro ao buscar cliente:', err);
            }
          }

          // Buscar itens
          let itens: any[] = [];
          try {
            const { data: itensData, error: itensError } = await supabase
              .from('itens_nota_fiscal')
              .select('*')
              .eq('nota_fiscal_id', nota.id);

            if (!itensError && itensData) {
              itens = itensData;
            }
          } catch (err) {
            console.warn('[notasFiscaisService] Erro ao buscar itens:', err);
          }

          // Montar objeto completo
          const notaCompleta = {
            ...nota,
            empresas: empresaData,
            empresas_empresa_id: empresaData,
            clientes: clienteData,
            clientes_cliente_id: clienteData,
            itens_nota_fiscal: itens,
          };

          return formatNotaFiscal(notaCompleta);
        } catch (err) {
          console.error('[notasFiscaisService] Erro ao formatar nota:', nota.id, err);
          // Retornar nota básica sem relacionamentos
          return formatNotaFiscal({
            ...nota,
            empresas: null,
            clientes: null,
            itens_nota_fiscal: [],
          });
        }
      })
    );

    return notasCompletas.filter((n): n is NotaFiscal => n !== null);
  } catch (error) {
    console.error('[notasFiscaisService] Erro geral ao buscar notas fiscais:', error);
    // Retornar array vazio em vez de quebrar
    return [];
  }
}

// Buscar nota fiscal por ID
export async function getNotaFiscalById(id: string): Promise<NotaFiscal | null> {
  try {
    const { data: nota, error } = await supabase
      .from('notas_fiscais')
      .select(`
        *,
        empresas:empresa_id (
          id,
          razao_social,
          nome_fantasia,
          cnpj,
          inscricao_estadual,
          email,
          telefone,
          enderecos (*)
        ),
        clientes:cliente_id (
          id,
          nome,
          tipo,
          cpf,
          cnpj,
          email,
          telefone,
          enderecos (*)
        ),
        itens_nota_fiscal (*)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    if (!nota) return null;

    return formatNotaFiscal(nota);
  } catch (error) {
    console.error('Erro ao buscar nota fiscal:', error);
    throw error;
  }
}

// Criar nova nota fiscal
export async function createNotaFiscal(
  nota: Omit<NotaFiscal, 'id' | 'createdAt' | 'updatedAt'>,
  contaId: string,
  empresaId: string
): Promise<NotaFiscal> {
  try {
    // Criar a nota fiscal
    const { data: novaNota, error: notaError } = await supabase
      .from('notas_fiscais')
      .insert({
        tipo: nota.tipo,
        numero: nota.numero,
        serie: nota.serie,
        chave_acesso: nota.chaveAcesso || null,
        empresa_id: empresaId,
        cliente_id: nota.destinatario.id,
        conta_id: contaId,
        data_emissao: nota.dataEmissao,
        data_vencimento: nota.dataVencimento || null,
        subtotal: nota.subtotal,
        desconto: nota.desconto || 0,
        total: nota.total,
        status: nota.status,
        forma_pagamento: nota.formaPagamento || null,
      })
      .select()
      .single();

    if (notaError) throw notaError;

    // Criar os itens da nota
    if (nota.itens && nota.itens.length > 0) {
      const itens = nota.itens.map(item => ({
        nota_fiscal_id: novaNota.id,
        tipo: item.tipo,
        produto_id: item.produtoId || null,
        servico_id: item.servicoId || null,
        descricao: item.descricao,
        quantidade: item.quantidade,
        unidade_medida: item.unidadeMedida,
        valor_unitario: item.valorUnitario,
        valor_total: item.valorTotal,
        ncm: item.ncm || null,
        cfop: item.cfop || null,
      }));

      const { error: itensError } = await supabase
        .from('itens_nota_fiscal')
        .insert(itens);

      if (itensError) throw itensError;
    }

    // Buscar a nota completa com relacionamentos
    return await getNotaFiscalById(novaNota.id);
  } catch (error) {
    console.error('Erro ao criar nota fiscal:', error);
    throw error;
  }
}

// Atualizar nota fiscal
export async function updateNotaFiscal(id: string, nota: Partial<NotaFiscal>): Promise<NotaFiscal> {
  try {
    const updateData: any = {};
    
    if (nota.tipo) updateData.tipo = nota.tipo;
    if (nota.numero) updateData.numero = nota.numero;
    if (nota.serie) updateData.serie = nota.serie;
    if (nota.chaveAcesso !== undefined) updateData.chave_acesso = nota.chaveAcesso || null;
    if (nota.dataEmissao) updateData.data_emissao = nota.dataEmissao;
    if (nota.dataVencimento !== undefined) updateData.data_vencimento = nota.dataVencimento || null;
    if (nota.subtotal !== undefined) updateData.subtotal = nota.subtotal;
    if (nota.desconto !== undefined) updateData.desconto = nota.desconto;
    if (nota.total !== undefined) updateData.total = nota.total;
    if (nota.status) updateData.status = nota.status;
    if (nota.formaPagamento !== undefined) updateData.forma_pagamento = nota.formaPagamento || null;

    const { error } = await supabase
      .from('notas_fiscais')
      .update(updateData)
      .eq('id', id);

    if (error) throw error;

    // Atualizar itens se fornecidos
    if (nota.itens) {
      // Deletar itens existentes
      await supabase
        .from('itens_nota_fiscal')
        .delete()
        .eq('nota_fiscal_id', id);

      // Inserir novos itens
      if (nota.itens.length > 0) {
        const itens = nota.itens.map(item => ({
          nota_fiscal_id: id,
          tipo: item.tipo,
          produto_id: item.produtoId || null,
          servico_id: item.servicoId || null,
          descricao: item.descricao,
          quantidade: item.quantidade,
          unidade_medida: item.unidadeMedida,
          valor_unitario: item.valorUnitario,
          valor_total: item.valorTotal,
          ncm: item.ncm || null,
          cfop: item.cfop || null,
        }));

        const { error: itensError } = await supabase
          .from('itens_nota_fiscal')
          .insert(itens);

        if (itensError) throw itensError;
      }
    }

    return await getNotaFiscalById(id);
  } catch (error) {
    console.error('Erro ao atualizar nota fiscal:', error);
    throw error;
  }
}

// Deletar nota fiscal
export async function deleteNotaFiscal(id: string): Promise<void> {
  try {
    // Deletar itens primeiro (cascade deve fazer isso automaticamente, mas vamos garantir)
    await supabase
      .from('itens_nota_fiscal')
      .delete()
      .eq('nota_fiscal_id', id);

    // Deletar nota fiscal
    const { error } = await supabase
      .from('notas_fiscais')
      .delete()
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    console.error('Erro ao deletar nota fiscal:', error);
    throw error;
  }
}

// Buscar notas fiscais por termo (para busca global)
export async function searchNotasFiscais(term: string, empresaId: string): Promise<NotaFiscal[]> {
  try {
    const { data: notas, error } = await supabase
      .from('notas_fiscais')
      .select(`
        *,
        empresas:empresa_id (
          id,
          razao_social,
          nome_fantasia,
          cnpj,
          inscricao_estadual,
          email,
          telefone,
          enderecos (*)
        ),
        clientes:cliente_id (
          id,
          nome,
          tipo,
          cpf,
          cnpj,
          email,
          telefone,
          enderecos (*)
        ),
        itens_nota_fiscal (*)
      `)
      .eq('empresa_id', empresaId)
      .or(`numero.ilike.%${term}%,serie.ilike.%${term}%,chave_acesso.ilike.%${term}%`)
      .limit(20);

    if (error) throw error;

    return (notas || []).map((nota: any) => formatNotaFiscal(nota));
  } catch (error) {
    console.error('Erro ao buscar notas fiscais:', error);
    throw error;
  }
}

// Função auxiliar para formatar nota fiscal do banco para o formato da aplicação
function formatNotaFiscal(nota: any): NotaFiscal | null {
  try {
    // Formatar emitente (empresa)
    const emitente = nota.empresas || nota.empresas_empresa_id || null;
    const enderecoEmitente = emitente?.enderecos || {};

    // Formatar destinatário (cliente)
    const destinatario = nota.clientes || nota.clientes_cliente_id || null;
    const enderecoDestinatario = destinatario?.enderecos || {};
    
    // Se não há emitente ou destinatário, retornar null
    if (!emitente || !destinatario) {
      console.warn('[notasFiscaisService] Nota sem emitente ou destinatário:', nota.id);
      return null;
    }

  // Formatar itens
  const itens: ItemNota[] = (nota.itens_nota_fiscal || []).map((item: any) => ({
    id: item.id,
    tipo: item.tipo,
    produtoId: item.produto_id || undefined,
    servicoId: item.servico_id || undefined,
    descricao: item.descricao,
    quantidade: item.quantidade,
    unidadeMedida: item.unidade_medida,
    valorUnitario: item.valor_unitario,
    valorTotal: item.valor_total,
    ncm: item.ncm || undefined,
    cfop: item.cfop || undefined,
  }));

  return {
    id: nota.id,
    tipo: nota.tipo as 'NFe' | 'NFSe' | 'NFCe',
    numero: nota.numero,
    serie: nota.serie,
    chaveAcesso: nota.chave_acesso || undefined,
    emitente: {
      id: emitente?.id || '',
      razaoSocial: emitente?.razao_social || '',
      nomeFantasia: emitente?.nome_fantasia || '',
      cnpj: emitente?.cnpj || '',
      inscricaoEstadual: emitente?.inscricao_estadual || '',
      email: emitente?.email || '',
      telefone: emitente?.telefone || '',
      endereco: {
        cep: enderecoEmitente.cep || '',
        logradouro: enderecoEmitente.logradouro || '',
        numero: enderecoEmitente.numero || '',
        complemento: enderecoEmitente.complemento || undefined,
        bairro: enderecoEmitente.bairro || '',
        cidade: enderecoEmitente.cidade || '',
        estado: enderecoEmitente.estado || '',
      },
    },
    destinatario: {
      id: destinatario?.id || '',
      nome: destinatario?.nome || '',
      tipo: destinatario?.tipo || 'F',
      cpf: destinatario?.cpf || undefined,
      cnpj: destinatario?.cnpj || undefined,
      email: destinatario?.email || '',
      telefone: destinatario?.telefone || '',
      endereco: {
        cep: enderecoDestinatario.cep || '',
        logradouro: enderecoDestinatario.logradouro || '',
        numero: enderecoDestinatario.numero || '',
        complemento: enderecoDestinatario.complemento || undefined,
        bairro: enderecoDestinatario.bairro || '',
        cidade: enderecoDestinatario.cidade || '',
        estado: enderecoDestinatario.estado || '',
      },
      createdAt: destinatario?.created_at || '',
      updatedAt: destinatario?.updated_at || '',
    },
    dataEmissao: nota.data_emissao,
    dataVencimento: nota.data_vencimento || undefined,
    itens,
    subtotal: nota.subtotal,
    desconto: nota.desconto || 0,
    total: nota.total,
    status: nota.status,
    formaPagamento: nota.forma_pagamento || undefined,
  };
  } catch (error) {
    console.error('[notasFiscaisService] Erro ao formatar nota fiscal:', error, nota);
    return null;
  }
}

